import classNames from "classnames";
import { AccountingMethodBanner } from "components/BooksSettings/AccountingMethodBanner";
import { CommentsSlider } from "components/CommentsSlider/CommentsSlider";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { BalanceMatchingBanner } from "components/DesignSystem/BalanceMatchingBanner/BalanceMatchingBanner";
import { Search } from "components/DesignSystem/Search/Search";
import GeneralLedgerAccordion from "components/GeneralLedger/GeneralLedgerAccordion/GeneralLedgerAccordion";
import { MoneyInTransitBanner } from "components/MoneyTransitBanner/MoneyInTransitBanner";
import { SliderAnimation } from "components/SliderAnimation/SliderAnimation";
import { TransactionSlider } from "components/Transaction/Slider/TransactionSlider";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { ACCOUNTING_METHOD_TYPES, GENERAL_LEDGER } from "constants/bookkeeping";
import { AnimatePresence, motion } from "framer-motion";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { parse, stringify } from "qs";
import { ChangeEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import {
  useGetGeneralLedgerQuery,
  useGetMoneyInTransitQuery,
} from "store/apis/generalLedger";
import { COMMENT_TYPE, NoteResponse } from "store/apis/notes";
import { LedgerReportType, setLedgerFilter } from "store/slices/generalLedger";
import { setOpenComment } from "store/slices/openComment";
import { RootState } from "store/store";
import { RowSelectionState } from "react-table-8.10.7";
import { GeneralLedger as GeneralLedgerType } from "types/Models/books";
import { debounce } from "utils/debouncing";
import { formatDate } from "utils/formatDate";
import { StartBookkeepingDateEmptyScreen } from "./GeneralLedgerEmptyScreen";
import { GeneralLedgerFilters } from "./GeneralLedgerFilters";
import { TableSelectionControls } from "./TableSelectionControls";
const getTransactionCategory = (
  ledger: GeneralLedgerType[],
  transactionId: string
): string | null => {
  for (const category of ledger) {
    const transaction = category.transactions?.find(
      (transaction) => transaction.uuid === transactionId
    );

    if (transaction) {
      return category.uuid;
    }

    if (category.types) {
      const foundCategory = getTransactionCategory(
        category.types,
        transactionId
      );
      if (foundCategory) {
        return foundCategory;
      }
    }
  }

  return null;
};

type LedgerContentProps = {
  generalLedgerData: GeneralLedgerType[] | undefined;
  selectedCategory: string | null;
  entityId: string;
  selectedRows: RowSelectionState;
  setSelectedRows: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  hasWritePermission: boolean;
};

const LedgerContent = ({
  generalLedgerData,
  selectedCategory,
  entityId,
  selectedRows,
  setSelectedRows,
  hasWritePermission,
}: LedgerContentProps) => {
  const ledgerData = generalLedgerData;

  return (
    <>
      <GeneralLedgerAccordion.Root className="t-pb-24 t-relative">
        <GeneralLedgerAccordion.Trigger
          data={ledgerData}
          openCategory={selectedCategory}
          entityId={entityId}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          hasWritePermission={hasWritePermission}
        />
      </GeneralLedgerAccordion.Root>
      <TransactionSlider />
      <TableSelectionControls
        deselectRows={() => setSelectedRows?.({})}
        allSelectedRows={selectedRows || {}}
        hasWritePermission={hasWritePermission}
      />
    </>
  );
};

export const GeneralLedger = ({
  commentPane,
}: {
  commentPane?: ReturnType<typeof useModal>;
}) => {
  usePageTitle("General Ledger");
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.ACCOUNTING
  );
  const { update, updateMultiple } = useUpdateQuery();
  const dispatch = useDispatch();
  const query = useQuery();
  const searchTerm = query.get("search_term");

  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});

  const openComment = useAppSelector((state) => state.openComment.openComment);

  const setActiveComment = (comment: NoteResponse) => {
    if (comment.transaction) {
      updateMultiple([
        {
          query: "selected_category_id",
          value: comment.transaction.category_id || null,
        },
        {
          query: "selected_transaction_id",
          value: comment.transaction.uuid || null,
        },
      ]);

      return dispatch(setOpenComment(comment.transaction.uuid));
    }

    if (comment.category) {
      updateMultiple([
        {
          query: "selected_category_id",
          value: comment.category?.uuid,
        },
        {
          query: "selected_transaction_id",
          value: null,
        },
      ]);

      return dispatch(setOpenComment(comment.category.uuid));
    }
  };

  const {
    filters: {
      cashFlow,
      minAmount,
      maxAmount,
      accountingMethod,
      startDate,
      endDate,
      hideZero,
      categoryIds,
    },
  } = useSelector((state: RootState) => state.ledgerFilter);

  const filterValues = {
    cashFlow,
    minAmount: ((minAmount.value || "") as string)
      ?.replaceAll("$", "")
      .replaceAll(",", ""),
    maxAmount: ((maxAmount.value || "") as string)
      ?.replaceAll("$", "")
      .replaceAll(",", ""),
    accountingMethod,
    startDate,
    endDate,
    hideZero,
    categoryIds,
    searchTerm: searchTerm,
    reportType: GENERAL_LEDGER as LedgerReportType,
  };

  const { data: moneyInTransit = [] } = useGetMoneyInTransitQuery(
    {
      groupId,
      entityId,
      startDate: startDate.value,
      endDate: endDate.value,
    },
    { skip: !groupId || !entityId, refetchOnMountOrArgChange: true }
  );

  const {
    data: generalLedgerRes,
    isSuccess,
    isFetching,
  } = useGetGeneralLedgerQuery(
    {
      groupId,
      entityId,
      ...filterValues,
    },
    { skip: !groupId || !entityId, refetchOnMountOrArgChange: true }
  );

  const handleChange = debounce((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    update({ query: "search_term", value: value || null });
    dispatch(setLedgerFilter({ search_term: value }));
  });

  const { ledger_data: generalLedgerData, balance_summary } =
    generalLedgerRes || {};

  const ledgerData = generalLedgerData as GeneralLedgerType[] | undefined;

  const isEmpty = !ledgerData || ledgerData.length === 0;

  const selectedTransaction = query.get("selected_transaction_id");
  const selectedCategory =
    selectedTransaction && ledgerData
      ? getTransactionCategory(ledgerData, selectedTransaction)
      : query.get("category") || query.get("selected_category_id");

  const selectedCategoryFromQuery = query.get("selected_category_id");
  const openCommentParam = query.get("open_comment");

  const location = useLocation();
  const history = useHistory();

  useEffect(() => {
    let currentSearch = parse(location.search, { ignoreQueryPrefix: true });

    let timeout: NodeJS.Timeout;
    if (selectedCategoryFromQuery || selectedTransaction) {
      if (currentSearch.selected_transaction_id) {
        delete currentSearch.selected_transaction_id;
      }

      if (selectedCategoryFromQuery) {
        delete currentSearch.selected_category_id;
      }

      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 3000);
    }

    return () => clearTimeout(timeout);
  }, [
    history,
    location.pathname,
    location.search,
    selectedCategoryFromQuery,
    selectedTransaction,
  ]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (openCommentParam) {
      dispatch(setOpenComment(openCommentParam));
      const currentSearch = parse(location.search, { ignoreQueryPrefix: true });
      delete currentSearch.open_comment;
      timeout = setTimeout(() => {
        history.replace(
          `${location.pathname}${stringify(currentSearch, {
            addQueryPrefix: true,
          })}`
        );
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [openCommentParam]);

  return (
    <motion.div
      layout
      className="t-flex t-h-full t-overflow-y-auto t-pl-5 sm:t-h-auto sm:t-overflow-visible sm:t-pl-3"
    >
      <div className="t-relative t-gap-5 t-flex t-flex-col t-flex-1 t-min-w-0">
        <DashboardContainer.Header
          sticky
          className={classNames("t-flex t-flex-col t-gap-4", {
            "t-pr-10 sm:t-pr-3": !commentPane?.isOpen,
            "t-pr-3": commentPane?.isOpen,
            "sm:!t-static": true,
          })}
        >
          <div>
            {moneyInTransit.length > 0 && (
              <MoneyInTransitBanner transactions={moneyInTransit} />
            )}
            <div className="t-bg-surface t-flex t-flex-col t-gap-4">
              <div className="t-flex t-gap-2 t-w-full t-items-center sm:t-flex-wrap">
                <Search
                  className="t-w-[18%] focus:t-w-3/6 t-transition-all t-self-start"
                  onChange={handleChange}
                  placeholder="Search"
                  defaultValue={searchTerm || ""}
                />
                <GeneralLedgerFilters entityId={entityId} />
              </div>
              {balance_summary && (
                <BalanceMatchingBanner
                  state={
                    balance_summary?.difference === 0 ? "success" : "error"
                  }
                  statusText={
                    balance_summary?.difference === 0
                      ? "Totals matching"
                      : "Totals not matching"
                  }
                  formulaText={
                    balance_summary?.difference === 0
                      ? "Debits = Credits"
                      : "Debits ≠ Credits"
                  }
                  dateRange={`${formatDate(startDate.value)} - ${formatDate(
                    endDate.value
                  )}`}
                >
                  <div className="t-flex t-flex-col t-gap-1">
                    <span className="t-text-text-30 t-text-body-sm">
                      Total Debits
                    </span>
                    <AmountSuperScript
                      amount={balance_summary?.total_debits || 0}
                    />
                  </div>
                  <span
                    className={classNames("t-self-center t-text-body-lg", {
                      "t-text-text-30": balance_summary?.difference === 0,
                      "t-text-red-70": balance_summary?.difference !== 0,
                    })}
                  >
                    {balance_summary?.difference === 0 ? "=" : "≠"}
                  </span>
                  <div className="t-flex t-flex-col t-gap-1">
                    <span className="t-text-text-30 t-text-body-sm">
                      Total Credits
                    </span>
                    <AmountSuperScript
                      amount={balance_summary?.total_credits || 0}
                    />
                  </div>
                </BalanceMatchingBanner>
              )}
              <AccountingMethodBanner
                selectedAccountingMethod={
                  filterValues.accountingMethod.value ??
                  (query.get("accountingMethod") as ACCOUNTING_METHOD_TYPES)
                }
                entityId={entityId}
              />
            </div>
          </div>
        </DashboardContainer.Header>

        <div
          className={classNames("t-overflow-x-auto sm:t-overflow-x-hidden", {
            "t-pr-6 sm:t-pr-3": !commentPane?.isOpen,
            "t-pr-3": commentPane?.isOpen,
            "t-h-full": isEmpty,
          })}
        >
          <Async.Root {...{ isLoading: isFetching, isEmpty, isSuccess }}>
            <Async.Empty>
              <StartBookkeepingDateEmptyScreen />
            </Async.Empty>
            <Async.Success>
              <LedgerContent
                generalLedgerData={ledgerData}
                selectedCategory={selectedCategory}
                entityId={entityId}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                hasWritePermission={hasWritePermission}
              />
            </Async.Success>
          </Async.Root>
        </div>
      </div>
      <AnimatePresence>
        {commentPane?.isOpen && (
          <SliderAnimation className="t-h-full t-shrink-0 t-grow-0 t-sticky t-top-0 t-border-0 t-border-l t-border-solid t-border-neutral-0 t-overflow-y-auto t-p-4 t-pt-0 sm:t-fixed sm:t-inset-0 sm:t-z-modal sm:t-bg-surface sm:!t-w-full">
            <CommentsSlider
              activeComment={openComment}
              setActiveComment={setActiveComment}
              onClose={commentPane?.toggle}
              commentTypes={[COMMENT_TYPE.GENERAL_LEDGER_COMMENT]}
              generalLedgerFilters={filterValues}
            />
          </SliderAnimation>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
